import { useParams } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';
import { useElapsedTime } from 'use-elapsed-time';
import { Button } from '@progress/kendo-react-buttons';
import React, { JSX, useContext, useEffect, useRef, useState } from 'react';
import { useGame } from '../../hooks/use-game';

import {
  pauseGameState,
  resumeGameState,
  startGameState,
  stopGameState
} from '../../services/games';
import {
  EndGameAsync,
  StartGameAsync,
  PausedGameAsync,
  ResumeGameAsync
} from '../../utils/game-engine/game';
import { GameStatus } from '../../types/state';
import { formatTimer } from '../../utils/timer';
import { GameContext } from '../../contexts/game';
import { ElapsedTime } from '../../types/state/game/game-state';
import { ConfirmationDialog } from '../dialog/confirmation-dialog';
import { UpdateGameStateAsync } from '../../utils/game-engine/base';
import { ManualScoring } from '../../types/manual-scoring';

export const GameControl: React.FC<{ noBorder?: boolean }> = ({ noBorder }) => {
  const { gameCode } = useParams();
  const [game, setGame] = useContext(GameContext);
  const { data: gameEntity } = useGame(gameCode!, true);

  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isShowConfirmation, setIsShowConfirmation] = useState<boolean>(false);
  const [taskScorings, , removeTaskScorings] = useLocalStorage<ManualScoring[]>(
    `task-scorings-${gameCode}`,
    []
  );
  const { elapsedTime, reset } = useElapsedTime({
    isPlaying,
    updateInterval: 1
  });
  const hasSyncedServerTimeRef = useRef<boolean>(false);

  const handleStartClick = () => {
    startGameState(game.gameCode!)
      .then(() => StartGameAsync(game.gameState!))
      .then((response) => {
        setGame((prev) => UpdateGameStateAsync(prev, response));
        hasSyncedServerTimeRef.current = true;
        setIsPlaying(true);
      });
  };

  const handleEndClick = () => setIsShowConfirmation(true);

  const handlePauseClick = () => {
    pauseGameState(game.gameCode!)
      .then(() => PausedGameAsync(game.gameState!, game.gameState?.status!))
      .then((response) =>
        setGame((prev) => UpdateGameStateAsync(prev, response))
      );
  };

  const handleResumeClick = () => {
    resumeGameState(game.gameCode!)
      .then(() => ResumeGameAsync(game.gameState!))
      .then((response) =>
        setGame((prev) => UpdateGameStateAsync(prev, response))
      );
  };

  const handleOnCancelClick = () => setIsShowConfirmation(false);
  const handleOnContinueClick = () => {
    stopGameState(game.gameCode!)
      .then(() => EndGameAsync(game.gameState!))
      .then((response) => {
        setGame((prev) => UpdateGameStateAsync(prev, response));
        handleOnCancelClick();
        setIsPlaying(false);
        hasSyncedServerTimeRef.current = false;
        taskScorings.length === 0 && removeTaskScorings();
      });
  };

  const renderButton = (): JSX.Element => {
    const status: GameStatus = game?.gameState?.status ?? 'PreGame';
    if (status === 'PreGame' || status === 'Paused') {
      return (
        <>
          <Button
            themeColor={'success'}
            className={'w-10 ml-2'}
            onClick={
              status === 'PreGame' ? handleStartClick : handleResumeClick
            }>
            {status === 'PreGame' ? 'Start Game' : 'Resume Game'}
          </Button>
          <Button
            themeColor={status === 'PreGame' ? 'base' : 'error'}
            className={'w-10 ml-2'}
            onClick={handleEndClick}
            disabled={status === 'PreGame'}>
            End Game
          </Button>
        </>
      );
    } else if (
      status === 'Starting' ||
      status === 'Running' ||
      status === 'PostGame'
    ) {
      return (
        <>
          <Button
            themeColor={'info'}
            className={'w-10 ml-2'}
            onClick={handlePauseClick}>
            Pause Game
          </Button>
          <Button
            themeColor={'error'}
            className={'w-10 ml-2'}
            onClick={handleEndClick}>
            End Game
          </Button>
        </>
      );
    }
    return <span className={'text-danger'}>Game Over</span>;
  };

  const calculateLastElapsedTime = (): number => {
    const status =
      game.gameState?.status ?? gameEntity?.gameStatus ?? 'PreGame';
    if (status === 'PreGame') {
      return 0;
    }

    if (game.gameState?.elapsedTime && game.gameState.elapsedTime.length > 0) {
      const time: number = game.gameState.elapsedTime
        .map<number>((item: ElapsedTime) => {
          const endTime: number =
            item.pausedTime !== undefined && item.pausedTime !== null
              ? new Date(item.pausedTime).getTime()
              : new Date().getTime();
          const startTime: number = new Date(item.startedTime).getTime();
          return endTime - startTime;
        })
        .reduce<number>((prev: number, next: number) => prev + next, 0);
      return Math.max(0, time / 1000);
    }

    // Fallback: Use game recorded start time if elapsedTime array is not yet loaded
    const recordedStartTime =
      game.gameState?.startedUtc || gameEntity?.actualStartDateUtc;

    const isRunning =
      status === 'Starting' ||
      status === 'Running' ||
      status === 'PostGame';

    if (isRunning && recordedStartTime) {
      const startTime = new Date(recordedStartTime).getTime();
      const now = new Date().getTime();
      return Math.max(0, (now - startTime) / 1000);
    }

    return 0;
  };

  useEffect(() => {
    const calculated = calculateLastElapsedTime();
    const isRunning =
      game.gameState?.status === 'Starting' ||
      game.gameState?.status === 'Running' ||
      game.gameState?.status === 'PostGame';

    // Synchronize timer if not yet synced with server time, if paused, or if server elapsed time arrived with drift
    if (
      calculated > 0 &&
      (!hasSyncedServerTimeRef.current ||
        game.gameState?.status === 'Paused' ||
        Math.abs(elapsedTime - calculated) > 3)
    ) {
      reset(calculated);
      hasSyncedServerTimeRef.current = true;
    }

    if (isRunning) {
      setIsPlaying(true);
    } else if (
      game.gameState?.status === 'Paused' ||
      game.gameState?.status === 'Finished'
    ) {
      setIsPlaying(false);
    }
    if (
      game.activityState &&
      !game.activityState.hasGame &&
      game.gameState?.status !== 'Finished' &&
      game.gameState?.status !== 'PreGame'
    ) {
      handleOnContinueClick();
    }
  }, [
    game.gameState?.status,
    game.gameState?.elapsedTime,
    game.gameState?.startedUtc,
    gameEntity?.actualStartDateUtc
  ]);

  return (
    <>
      <div
        className={`d-flex align-items-center justify-content-between ${noBorder ? '' : 'border-bottom py-3'}`}>
        <span className={'fw-bold'}>
          Game Timer :{' '}
          <span className={'text-danger ml-3'}>{formatTimer(elapsedTime)}</span>
        </span>
        <div>{renderButton()}</div>
      </div>
      {isShowConfirmation && (
        <ConfirmationDialog
          title={'Action needed'}
          message={'Are you sure you want to end the game?'}
          onCancelCallback={handleOnCancelClick}
          onContinueCallback={handleOnContinueClick}
        />
      )}
    </>
  );
};
